import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Layers, Plus, Trash2, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  CATEGORIES,
  CATEGORY_LABEL,
  CATEGORY_TONE,
  type DocumentCategory,
  type DocumentSummary,
} from '@/pages/documents/types';
import { DocumentUploadModal, type EligibleOwner } from './DocumentUploadModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const MONTH_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

const MONTH_LONG = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export function AdminDocuments() {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | 'ALL'>('ALL');
  const [yearFilter, setYearFilter] = useState<string>('');
  const [monthFilter, setMonthFilter] = useState<string>('');

  const owners = useQuery({
    queryKey: ['admin', 'documents', 'eligible-owners'],
    queryFn: () => api<EligibleOwner[]>('/admin/documents/eligible-owners'),
  });

  const documents = useQuery({
    queryKey: ['admin', 'documents', { ownerFilter, categoryFilter, yearFilter, monthFilter }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (ownerFilter) params.set('ownerUserId', ownerFilter);
      if (categoryFilter !== 'ALL') params.set('category', categoryFilter);
      if (yearFilter) params.set('periodYear', yearFilter);
      if (monthFilter) params.set('periodMonth', monthFilter);
      const qs = params.toString();
      return api<DocumentSummary[]>(`/admin/documents${qs ? `?${qs}` : ''}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api<void>(`/admin/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] }),
    onError: (err: unknown) => {
      window.alert(err instanceof ApiError ? err.message : 'Error al borrar');
    },
  });

  const activeUsers = useMemo(() => owners.data ?? [], [owners.data]);

  const rows = useMemo(() => documents.data ?? [], [documents.data]);

  // Selección múltiple — vive en el componente. Al cambiar los filtros (y por
  // tanto las filas visibles), purgamos los ids que ya no están en pantalla
  // para evitar acciones masivas sobre cosas que el usuario no ve.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(rows.map((r) => r.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows]);
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0 && !allSelected;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const bulkDownload = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      const res = await fetch(`${API_BASE_URL}/admin/documents/bulk-download`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const data: { message?: string | string[] } = await res.json().catch(() => ({}));
        const msg = Array.isArray(data.message) ? data.message[0] : data.message;
        throw new Error(msg ?? `Error ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i) ?? cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? decodeURIComponent(m[1]) : 'documentos.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err: unknown) => {
      window.alert(err instanceof Error ? err.message : 'Error al descargar');
    },
  });

  const bulkDelete = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      // Borrado individual en paralelo — el endpoint bulk no existe; el
      // soft-delete por id es barato y nos da retry parcial gratis.
      const results = await Promise.allSettled(
        ids.map((id) => api<void>(`/admin/documents/${id}`, { method: 'DELETE' })),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: ids.length, failed };
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
      setSelected(new Set());
      if (failed > 0) {
        window.alert(`Se borraron ${total - failed} de ${total}. ${failed} fallaron.`);
      }
    },
    onError: (err: unknown) => {
      window.alert(err instanceof Error ? err.message : 'Error al borrar');
    },
  });

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tightish">Documentos</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              Nóminas, contratos y otros documentos asignados a empleados. Cada empleado solo ve
              los suyos.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 flex-wrap">
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/documentos/importar">
                <Layers className="h-3.5 w-3.5" />
                Importar nóminas en lote
              </Link>
            </Button>
            <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Subir documento
            </Button>
          </div>
        </header>

        <div className="px-7 py-4 border-b border-border flex flex-wrap items-center gap-3 bg-paper/40">
          <FilterSelect
            label="Empleado"
            value={ownerFilter}
            onChange={setOwnerFilter}
            options={[
              { value: '', label: 'Todos' },
              ...activeUsers.map((u) => ({ value: u.id, label: u.fullName })),
            ]}
          />
          <FilterSelect
            label="Categoría"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as DocumentCategory | 'ALL')}
            options={[
              { value: 'ALL', label: 'Todas' },
              ...CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
            ]}
          />
          <FilterSelect
            label="Año"
            value={yearFilter}
            onChange={setYearFilter}
            options={[
              { value: '', label: 'Todos' },
              ...lastYears().map((y) => ({ value: String(y), label: String(y) })),
            ]}
          />
          <FilterSelect
            label="Mes"
            value={monthFilter}
            onChange={setMonthFilter}
            options={[
              { value: '', label: 'Todos' },
              ...MONTH_LONG.map((label, idx) => ({ value: String(idx + 1), label })),
            ]}
          />
        </div>

        {documents.isLoading ? (
          <p className="px-7 py-10 text-sm text-muted-foreground">Cargando…</p>
        ) : documents.error ? (
          <p role="alert" className="px-7 py-10 text-sm text-destructive">
            {documents.error instanceof ApiError
              ? documents.error.message
              : 'Error cargando documentos'}
          </p>
        ) : rows.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            Sin documentos para los filtros activos.
          </p>
        ) : (
          <>
            {selected.size > 0 ? (
              <div className="px-7 py-3 border-b border-border bg-icaria-100/50 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[13px] font-medium text-icaria-700">
                  {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => bulkDownload.mutate()}
                    disabled={bulkDownload.isPending || bulkDelete.isPending}
                  >
                    <Download className="h-3.5 w-3.5" />
                    {bulkDownload.isPending ? 'Preparando…' : `Descargar (${selected.size})`}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (
                        window.confirm(
                          `¿Borrar los ${selected.size} documentos seleccionados? Se moverán a la papelera (soft delete).`,
                        )
                      ) {
                        bulkDelete.mutate();
                      }
                    }}
                    disabled={bulkDownload.isPending || bulkDelete.isPending}
                    className="hover:border-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {bulkDelete.isPending ? 'Borrando…' : `Borrar (${selected.size})`}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelected(new Set())}
                    disabled={bulkDownload.isPending || bulkDelete.isPending}
                  >
                    <X className="h-3.5 w-3.5" />
                    Limpiar
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr>
                    <Th align="center">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todos"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={toggleAll}
                        className="h-4 w-4 accent-icaria-600 cursor-pointer"
                      />
                    </Th>
                    <Th>Empleado</Th>
                    <Th>Categoría</Th>
                    <Th>Documento</Th>
                    <Th align="center">Periodo</Th>
                    <Th align="right">Tamaño</Th>
                    <Th>Subido</Th>
                    <Th align="right" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((doc) => (
                    <AdminDocRow
                      key={doc.id}
                      doc={doc}
                      selected={selected.has(doc.id)}
                      onToggleSelect={() => toggleOne(doc.id)}
                      onDelete={() => {
                        if (window.confirm(`¿Borrar "${doc.title}"?`)) {
                          deleteMutation.mutate(doc.id);
                        }
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {uploadOpen ? (
        <DocumentUploadModal
          users={activeUsers}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
          }}
        />
      ) : null}
    </div>
  );
}

function AdminDocRow({
  doc,
  selected,
  onToggleSelect,
  onDelete,
}: {
  doc: DocumentSummary;
  selected: boolean;
  onToggleSelect: () => void;
  onDelete: () => void;
}) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/documents/${doc.id}/download`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('No se pudo descargar');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error al descargar');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <tr
      className={cn(
        'border-t border-border first:border-t-0 transition-colors',
        selected ? 'bg-icaria-100/40' : 'hover:bg-paper/40',
      )}
    >
      <Td align="center">
        <input
          type="checkbox"
          aria-label={`Seleccionar ${doc.title}`}
          checked={selected}
          onChange={onToggleSelect}
          className="h-4 w-4 accent-icaria-600 cursor-pointer"
        />
      </Td>
      <Td>
        <p className="font-medium">{doc.ownerName}</p>
      </Td>
      <Td>
        <Badge tone={CATEGORY_TONE[doc.category]} dot>
          {CATEGORY_LABEL[doc.category]}
        </Badge>
      </Td>
      <Td>
        <div className="flex items-center gap-2.5 min-w-0">
          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium truncate">{doc.title}</p>
            <p className="font-mono text-[11px] text-muted-foreground truncate">{doc.filename}</p>
          </div>
        </div>
      </Td>
      <Td align="center">
        {doc.periodMonth && doc.periodYear ? (
          <span className="font-mono text-[12px] tabular-nums uppercase">
            {MONTH_SHORT[doc.periodMonth - 1]} {doc.periodYear}
          </span>
        ) : doc.periodYear ? (
          <span className="font-mono text-[12px] tabular-nums">{doc.periodYear}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
      <Td align="right">
        <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
          {formatSize(doc.sizeBytes)}
        </span>
      </Td>
      <Td>
        <p className="text-[12px] text-muted-foreground leading-tight">
          {formatDate(doc.uploadedAt)}
        </p>
        <p className="text-[11px] text-muted-foreground leading-tight">por {doc.uploadedByName}</p>
      </Td>
      <Td align="right">
        <div className="inline-flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}>
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            aria-label="Borrar"
            className="hover:border-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </Td>
    </tr>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-[12.5px]">
      <span className="font-mono text-[10px] font-medium uppercase tracking-cap text-muted-foreground">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-sm border border-input bg-card px-2.5 text-[12.5px] focus-visible:border-icaria-600 focus-visible:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'center' | 'right' }) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase tracking-[0.08em] bg-paper px-5 py-3.5 text-muted-foreground border-b border-border',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children?: React.ReactNode; align?: 'center' | 'right' }) {
  return (
    <td
      className={cn(
        'px-5 py-4 align-middle',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </td>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTH_SHORT[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

function lastYears(): number[] {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2, y - 3];
}
